import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { creatorSeriesActions } from 'actions/series';
import { seriesHooks } from 'hooks';
import { LocalStorageUtils } from 'utils';

import InfoRow from './InfoRow';
import RecentRow from './RecentRow';
import SuggestedRow from './SuggestedRow';

import { When } from 'react-if';

import './StartingResults.scss';

const EMPTY_ARRAY = []
const StartingResults = ({ getResultsStyle, swapIsOpen }) => {
  const dispatch = useDispatch(),
        sources = useSelector(state => state.globalState.sources) || EMPTY_ARRAY,
        { suggested, selectedSymbols } = useSelector(state => state.seriesState.creatorState),
        [recents, setRecents] = useState(),
        setSuggested = useCallback(value => dispatch(creatorSeriesActions.onChange(value, "suggested")), [dispatch]),
        getEnvSuggested = seriesHooks.useEnvSuggested(),
        getSuggestedSymbolsRef = useRef(),
        getSuggestedSymbolsFileRef = useRef(),
        getSuggestedSymbols = useCallback(() => {
          if(suggested.length > 0) {
            return Promise.resolve();
          }
          getSuggestedSymbolsRef.current?.cancel?.()
          getSuggestedSymbolsFileRef.current?.cancel?.()
          return getEnvSuggested()
            .then(setSuggested)
        }, [getEnvSuggested, setSuggested, suggested.length]),
        addToBasket = useCallback(s => {
          dispatch(creatorSeriesActions.swapSelectedSymbol(s))
        }, [dispatch]),
        resultsStyle = useMemo(() => getResultsStyle(), [getResultsStyle]),
        arePatternsSelected = useMemo(() => selectedSymbols.some(s => s.pattern === "true" || s.pattern === undefined), [selectedSymbols]),
        selectedSource = useMemo(() => selectedSymbols?.length > 0 ? selectedSymbols[0]?.source : undefined, [selectedSymbols]),
        onRowClick = useCallback(symbol => dispatch(creatorSeriesActions.onAddRow({ symbol, selectedSymbols, addToBasket, arePatternsSelected })), [addToBasket, arePatternsSelected, dispatch, selectedSymbols]),
        handleOpenAddDatapointModal = useCallback(symbol => {
          dispatch(creatorSeriesActions.onChange(symbol, "datapointSymbol"))
          swapIsOpen()
        }, [dispatch, swapIsOpen]),
        availableSourcesNames = useMemo(() => sources.map(s => s?.name), [sources]),
        availableSuggested = useMemo(() => suggested.filter(s => availableSourcesNames.includes(s.source)), [availableSourcesNames, suggested]),
        unavailableSuggested = useMemo(() => suggested.filter(s => !availableSourcesNames.includes(s.source)), [availableSourcesNames, suggested])

  useEffect(() => {
    getSuggestedSymbols()
  }, [getSuggestedSymbols])

  useEffect(() => {
    setRecents(LocalStorageUtils.getRecentSymbols())
  }, [selectedSymbols?.length])

  useEffect(() => () => {
    getSuggestedSymbolsFileRef?.current?.cancel?.()
    getSuggestedSymbolsRef?.current?.cancel?.()
  }, [])

  return (
    <div 
        className="ng-office-app__authed__content__starting"
        style={resultsStyle}
    >
      <When condition={recents?.length > 0}>
        {() => (
          <div className="ng-office-app__authed__content__starting__block">
            <div className="ng-office-app__authed__content__starting__block__title">
              RECENT ITEMS
            </div>
            <div className="ng-office-app__authed__content__starting__block__content">
              {(recents || []).map((recentItem, recentIndex) => {
                const { 
                  isDirectlySelected,
                  isPatternSelected
                } = creatorSeriesActions.isInBasket(recentItem, selectedSymbols)
                return (
                  <RecentRow
                      addToBasket={onRowClick}
                      disabled={selectedSource && recentItem?.source !== selectedSource}
                      handleOpenAddDatapointModal={handleOpenAddDatapointModal}
                      isPatternSelected={isPatternSelected}
                      isSelected={isDirectlySelected}
                      key={recentIndex}
                      symbol={recentItem}
                      symbolIndex={recentIndex}
                  />
                )
              })}
            </div>
          </div>
        )}
      </When>
      <When condition={suggested?.length > 0}>
        {() => (
          <div className="ng-office-app__authed__content__starting__block">
            <div className="ng-office-app__authed__content__starting__block__title">
              SUGGESTED ITEMS
            </div>
            <div className="ng-office-app__authed__content__starting__block__content">
              {/* we show suggested symbols in two variants - one when user has access to env from which symbol comes, the normal variant, - second when symbol has source which user can not access, the info variant */}
              {availableSuggested.map((s, sIndex) => {
                const { 
                  isDirectlySelected,
                  isPatternSelected
                } = creatorSeriesActions.isInBasket(s, selectedSymbols)
                return (
                  <SuggestedRow
                      addToBasket={onRowClick}
                      disabled={selectedSource && s?.source !== selectedSource}
                      handleOpenAddDatapointModal={handleOpenAddDatapointModal}
                      isPatternSelected={isPatternSelected}
                      isSelected={isDirectlySelected}
                      key={sIndex}
                      symbol={s}
                      symbolIndex={sIndex}
                  />
                )
              })}
              {unavailableSuggested.map((s, sIndex) => (
                  <InfoRow
                      key={sIndex}
                      symbol={s}
                      symbolIndex={sIndex}
                  />
                )
              )}
            </div>
          </div>
        )}
      </When>
    </div>
  )
}

StartingResults.propTypes = {
  getResultsStyle: PropTypes.func.isRequired,
  swapIsOpen: PropTypes.func.isRequired
}

export default StartingResults;